/**
 * Sanitizes MathML in a given document.
 *
 * - Unwraps all `<mstyle>` tags.
 * - Ensures that `<mtd>` elements (cells) used in tables and matrices are left-aligned.
 * - Sets the font-family for certain `<mo>` elements (like brackets) and all `<msqrt>` elements to "math"
 *
 * @param document - The Document object containing MathML to sanitize.
 * @returns The sanitized Document object.
 */
export function sanitizeMathMl(document: Document) {
  // Unwrap all mstyle tags
  document.querySelectorAll("mstyle").forEach((el) => {
    el.replaceWith(...Array.from(el.children))
  })

  // Ensure that mtd elements (cells) used in tables and matrices are left-aligned
  document.querySelectorAll("mtd").forEach((mtd) => {
    mtd.style.textAlign = "left"
  })

  // Set font-family for matrices
  document.querySelectorAll("mo").forEach((mo) => {
    if (
      mo.textContent === "[" ||
      mo.textContent === "]" ||
      mo.textContent === "(" ||
      mo.textContent === ")"
    ) {
      mo.style.fontFamily = "math"
    }
  })

  // Set font-family for square roots
  document.querySelectorAll("msqrt").forEach((msqrt) => {
    msqrt.style.fontFamily = "math"
  })

  return document
}

const IGNORED_TTS_ELEMENTS = ["img", "svg", "figure", "sup", "a:has(sup)"] as const

/**
 * Removes elements that are not suitable for text-to-speech (TTS) from the document. See {@link IGNORED_TTS_ELEMENTS} for which tags are removed.
 *
 * @param document - The Document object to sanitize.
 * @returns The sanitized Document object with TTS-ignored elements removed.
 */
export function sanitizeTTS(document: Document) {
  document
    .querySelectorAll(IGNORED_TTS_ELEMENTS.join(", "))
    .forEach(node => node.remove())

  return document
}

/**
 * Removes the first `<h1>` element from the document.
 *
 * @param document - The Document object from which to remove the first `<h1>`.
 * @return The Document object with the first `<h1>` removed.
 */
export function removeFirstH1(document: Document) {
  document.querySelector("h1")?.remove()

  return document
}
